import React, { useEffect, useRef, useState } from 'react';
import { SunIcon, MoonIcon } from 'lucide-react';
type EclipseModalProps = {
  open: boolean;
  onClose: () => void;
};
const SUN_RADIUS = 92;
const INTRO_MS = 1600;
// The transit rushes to 70% and then crawls through the final stretch.
const FAST_MS = 2000;
const SLOW_MS = 7000;
const MESSAGE_DELAY_MS = 1000;
const AISCO_LOGO = 'https://uploadthingy.s3.us-west-1.amazonaws.com/cZrMyYGysL7x2nzbnxUSuo/White_Drop_for_Whatsapp.png';

/**
 * Area of the lens-shaped intersection of two equal circles of radius r
 * whose centers are separated by distance d, expressed as a fraction of
 * the sun's disc. Used to translate an obscuration percentage into the
 * real geometric offset of the moon.
 */
function coverageForOffset(d: number, r: number): number {
  if (d <= 0) return 1;
  if (d >= 2 * r) return 0;
  const area = 2 * r * r * Math.acos(d / (2 * r)) - d / 2 * Math.sqrt(4 * r * r - d * d);
  return area / (Math.PI * r * r);
}
function offsetForCoverage(fraction: number, r: number): number {
  if (fraction >= 1) return 0;
  if (fraction <= 0) return 2 * r;
  let low = 0;
  let high = 2 * r;
  for (let i = 0; i < 40; i++) {
    const mid = (low + high) / 2;
    if (coverageForOffset(mid, r) > fraction) low = mid;else high = mid;
  }
  return (low + high) / 2;
}
function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}
function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
/** Two-speed transit: quick up to 70%, then a long, deliberate slide to totality. */
function transitPercent(elapsed: number): number {
  if (elapsed <= 0) return 0;
  if (elapsed < FAST_MS) return 70 * easeOut(elapsed / FAST_MS);
  const slow = Math.min(1, (elapsed - FAST_MS) / SLOW_MS);
  return 70 + 30 * easeInOut(slow);
}
function phaseLabel(percent: number): string {
  if (percent >= 100) return 'Totalidad';
  if (percent >= 90) return 'Casi total';
  if (percent >= 40) return 'Parcial profunda';
  if (percent > 0) return 'Parcial';
  return 'Sin eclipse';
}
const STARS = [[12, 22], [26, 12], [42, 30], [58, 16], [72, 34], [86, 20], [18, 62], [34, 78], [64, 70], [82, 60], [92, 80], [8, 44]];
export function EclipseModal({
  open,
  onClose
}: EclipseModalProps) {
  const [percent, setPercent] = useState(0);
  // 0 → the home screen is untouched; 1 → the sun has fully materialised.
  const [intro, setIntro] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showMessage, setShowMessage] = useState(false);
  const [messageVisible, setMessageVisible] = useState(false);
  const exitRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    exitRef.current?.focus();
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);
  // Timeline: the sun fades in over the home screen, then the moon transits it.
  useEffect(() => {
    if (!open || !isPlaying) return;
    let frame = 0;
    const start = performance.now();
    const step = (now: number) => {
      const elapsed = now - start;
      setIntro(easeInOut(Math.min(1, elapsed / INTRO_MS)));
      if (elapsed >= INTRO_MS) {
        const value = transitPercent(elapsed - INTRO_MS);
        setPercent(Math.round(value));
        if (value >= 100) {
          setIsPlaying(false);
          setShowMessage(true);
          return;
        }
      }
      frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [open, isPlaying]);
  // Hold on totality for a beat before the message lands.
  useEffect(() => {
    if (!showMessage) return;
    const id = window.setTimeout(() => setMessageVisible(true), MESSAGE_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [showMessage]);
  if (!open) return null;
  const fraction = percent / 100;
  const offset = offsetForCoverage(fraction, SUN_RADIUS);
  const isTotal = percent >= 100;
  // Sky darkens progressively, and collapses quickly in the final stretch.
  const skyDarkness = Math.min(1, Math.pow(fraction, 3));
  // Everything stays translucent so the home screen keeps showing through.
  const backdropAlpha = (0.18 + 0.45 * skyDarkness) * intro;
  const skyAlpha = (0.22 + 0.55 * skyDarkness) * intro;
  const skyLightness = Math.round(30 - 26 * skyDarkness);
  const handleScrub = (value: number) => {
    setIsPlaying(false);
    setIntro(1);
    setPercent(value);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Eclipse solar del 12 de agosto" data-id="element-3164" data-mp="29mtjhcaz08q8">
      <div className="absolute inset-0" style={{
      backgroundColor: `rgba(5, 10, 22, ${backdropAlpha})`
    }} onClick={onClose} aria-hidden="true" data-id="element-3165" data-mp="7uf5upfglqfm" />

      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-white/10 shadow-2xl backdrop-blur-md" style={{
      backgroundColor: `rgba(15, 29, 58, ${0.5 * intro})`,
      opacity: 0.2 + 0.8 * intro
    }} data-id="element-3166" data-mp="3u509dvkx21a">
        <button ref={exitRef} onClick={onClose} className="absolute right-4 top-3 z-20 rounded text-xs font-medium tracking-wide text-white/60 transition-colors hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-3167" data-mp="1qn6yzn9yiflh">
          Exit
        </button>

        {/* Sky */}
        <div className="relative flex h-64 items-center justify-center" style={{
        backgroundColor: `hsla(220, 62%, ${skyLightness}%, ${skyAlpha})`
      }} data-id="element-3168" data-mp="12tnp92l2oh57">
          {/* Stars appear as the sky collapses */}
          <div className="pointer-events-none absolute inset-0" style={{
          opacity: skyDarkness * intro
        }} aria-hidden="true" data-id="element-3169" data-mp="1bulrpgfv4glo">
            {STARS.map(([x, y]) => <span key={`${x}-${y}`} className="absolute h-[3px] w-[3px] rounded-full bg-white" style={{
            left: `${x}%`,
            top: `${y}%`
          }} data-id="element-3170" data-mp="2n6wd31sa1ag3" />)}
          </div>

          <svg viewBox="0 0 320 240" className="relative h-full w-full" role="img" aria-label={`Simulación del eclipse con ${percent}% de solapamiento`} data-id="element-3171" data-mp="s2yc5mntbeeb">
            <defs data-id="element-3172" data-mp="12275qeermr05">
              <mask id="eclipse-mask" data-id="element-3173" data-mp="122pbc1nbihkq">
                <rect x="0" y="0" width="320" height="240" fill="black" data-id="element-3174" data-mp="121nwrn73fcy9" />
                <circle cx="160" cy="120" r={SUN_RADIUS} fill="white" data-id="element-3175" data-mp="x4nrhei2ie87" />
                <circle cx={160 - offset} cy="120" r={SUN_RADIUS} fill="black" data-id="element-3176" data-mp="9d8jp67iv4pk" />
              </mask>
              {/* Solar disc: incandescent core melting into its own radiation */}
              <radialGradient id="sun-body" cx="50%" cy="50%" r="50%" data-id="element-3177" data-mp="j3l5t2uzj5xt">
                <stop offset="0%" stopColor="#FFFDF0" stopOpacity="1" data-id="element-3178" data-mp="j5o2g5qn7xbi" />
                <stop offset="38%" stopColor="#FFE81A" stopOpacity="0.95" data-id="element-3179" data-mp="ec32w99eiva7" />
                <stop offset="74%" stopColor="#FFD000" stopOpacity="0.7" data-id="element-3180" data-mp="3mrwimlvws2q7" />
                <stop offset="100%" stopColor="#FFC400" stopOpacity="0" data-id="element-3181" data-mp="3htl808xsho6x" />
              </radialGradient>
              <radialGradient id="sun-radiation" cx="50%" cy="50%" r="50%" data-id="element-3182" data-mp="1xbsw080hvwrx">
                <stop offset="45%" stopColor="#FFE81A" stopOpacity="0.5" data-id="element-3183" data-mp="1x9pv2sxt0j8f" />
                <stop offset="72%" stopColor="#FFD84D" stopOpacity="0.22" data-id="element-3184" data-mp="223u2fdazeta1" />
                <stop offset="100%" stopColor="#FFD84D" stopOpacity="0" data-id="element-3185" data-mp="1obw1vqvc65ph" />
              </radialGradient>
              <filter id="corona-blur" data-id="element-3186" data-mp="1sdhi9m72x24f">
                <feGaussianBlur stdDeviation="10" data-id="element-3187" data-mp="1sa3hujhz72zm" />
              </filter>
              <filter id="sun-glow" data-id="element-3188" data-mp="2g2jz4rtw2qxv">
                <feGaussianBlur stdDeviation="10" data-id="element-3189" data-mp="2l1504tgytend" />
              </filter>
            </defs>

            {/* Outer radiation, unmasked so light still bleeds around the moon */}
            <circle cx="160" cy="120" r={SUN_RADIUS + 46} fill="url(#sun-radiation)" opacity={(0.85 - 0.55 * fraction) * intro} data-id="element-3190" data-mp="273gh7r6gkhky" />

            {/* Inner glow tied to whatever part of the disc is still exposed */}
            <circle cx="160" cy="120" r={SUN_RADIUS + 12} fill="url(#sun-body)" filter="url(#sun-glow)" opacity={0.6 * (1 - fraction) * intro} mask="url(#eclipse-mask)" data-id="element-3191" data-mp="vojp28ez9c9d" />

            {/* Corona, only meaningful near totality */}
            <circle cx="160" cy="120" r={SUN_RADIUS + 14} fill="#FFFBE6" filter="url(#corona-blur)" opacity={(isTotal ? 0.6 : Math.max(0, (fraction - 0.9) * 3)) * intro} data-id="element-3192" data-mp="1n98ndv2j1ic1" />

            {/* Visible crescent of the sun */}
            <circle cx="160" cy="120" r={SUN_RADIUS} fill="url(#sun-body)" opacity={0.92 * intro} mask="url(#eclipse-mask)" data-id="element-3193" data-mp="1dcm1d2ygsl8f" />

            {/* Moon silhouette */}
            <circle cx={160 - offset} cy="120" r={SUN_RADIUS} fill="#0B1220" opacity={offset >= 2 * SUN_RADIUS ? 0 : 0.82 * intro} data-id="element-3194" data-mp="260hyg8ry4f5q" />

            {/* AISCO drop resting inside the dark disc at totality */}
            <image href={AISCO_LOGO} x={160 - offset - 30} y={90} width={60} height={60} preserveAspectRatio="xMidYMid meet" className="transition-opacity duration-1000" opacity={isTotal ? 1 : 0} data-id="element-3195" data-mp="1wayeezshyf9a" />
          </svg>
        </div>

        {/* Controls, swapped for the closing message once totality settles */}
        <div className={`relative p-6 transition-all duration-700 ${showMessage ? 'min-h-[260px]' : ''}`} data-id="element-3196" data-mp="2was7ks9ecdfe">
          <div className={`transition-opacity duration-700 ${messageVisible ? 'pointer-events-none opacity-0' : 'opacity-100'}`} data-id="element-3197" data-mp="318jxxhue7rhh">
            <p className="text-xs font-medium uppercase tracking-wide text-white/70" data-id="element-3198" data-mp="366v8jusii60r">
              12 de agosto · Eclipse solar
            </p>

            <div className="mt-4 flex items-end justify-between" data-id="element-3199" data-mp="3f9e3zttyxgcz">
              <div data-id="element-3200" data-mp="3f71pjqnf5o4o">
                <span className="text-4xl font-semibold text-white tabular-nums" data-id="element-3201" data-mp="3k87klv4jxnhd">
                  {percent}%
                </span>
                <span className="ml-2 text-sm text-white/70" data-id="element-3202" data-mp="3ozz27g5qcivd">
                  de cobertura
                </span>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-medium ${isTotal ? 'bg-[#FFE81A] text-[#0B1220]' : 'bg-white/10 text-white/80'}`} data-id="element-3203" data-mp="3tx8xvdjppqrn">
                {phaseLabel(percent)}
              </span>
            </div>

            <label htmlFor="eclipse-range" className="sr-only" data-id="element-3204" data-mp="2pfwx7o3yj1uy">
              Porcentaje de eclipse
            </label>
            <input id="eclipse-range" type="range" min={0} max={100} step={1} value={percent} onChange={e => handleScrub(Number(e.target.value))} className="mt-4 w-full accent-[#FFE81A]" data-id="element-3205" data-mp="2uegirnen2bms" />

            <div className="mt-1 flex items-center justify-between text-xs text-white/70" data-id="element-3206" data-mp="344jbrnlrfpl4">
              <span className="flex items-center gap-1" data-id="element-3207" data-mp="3945xymjkgg62">
                <SunIcon size={12} data-id="element-3208" data-mp="393mf93m3dnqz" /> Sol despejado
              </span>
              <span className="flex items-center gap-1" data-id="element-3209" data-mp="3d4g3xz7cz6kx">
                Totalidad <MoonIcon size={12} data-id="element-3210" data-mp="3i4u0ajzlks34" />
              </span>
            </div>
          </div>

          {showMessage && <div className={`absolute inset-0 flex flex-col items-center justify-center px-8 text-center transition-opacity duration-1000 ${messageVisible ? 'opacity-100' : 'opacity-0'}`} data-id="element-3211" data-mp="3n3f5kxtpi1yf">
              <h2 className="text-lg font-semibold leading-snug text-white" data-id="element-3212" data-mp="1spe4e6aop2v6">
                Even when the light fades, growth never stops.
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-white/75" data-id="element-3213" data-mp="1l7fy0ansu7w6">
                AISCO pivots keep every field moving forward — delivering water
                with precision, reliability, and performance where it matters
                most.
              </p>
              <p className="mt-5 text-xl font-semibold tracking-wide text-[#FFE81A]" data-id="element-3214" data-mp="16ia1wvk9cxsi">
                Keep growing. Keep moving. Keep AISCO.
              </p>
            </div>}
        </div>
      </div>
    </div>;
}